import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import AlertBox from '../../Components/AlertBox';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

const EmployeeReport = () => {
  // Initialize state with empty values
  const [employeeData, setEmployeeData] = useState({
    fullName: "",
    selectedEmployeeId: "",
    department: "",
    designation: "",
    email: "",
    phone: "",
    emergencyMobileNumber: "",
    profileImage: "",
    adharImage: "",
    panImage: "",
    aadharNumber: "",
    panNumber: "",
    userUpi: "",
    dateOfJoining: "",
    employmentStatus: "",
    reportingManager: "",
    employmentType: "",
    performanceRating: 0,
    skills: [],
    projects: [],
    workHistory: [],
    documents: [],
    attendance: {
      present: 0,
      absent: 0,
      late: 0,
      leaveBalance: {
        sickLeave: 0,
        casualLeave: 0,
        annualLeave: 0
      }
    }
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [departments, setDepartments] = useState({});
  const [designations, setDesignations] = useState({});
  const [imageModal, setImageModal] = useState({ isOpen: false, imageUrl: '', title: '' });
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  // NEW: States for employee selection (for company/superadmin)
  const [allEmployees, setAllEmployees] = useState([]);
  const [showEmployeeSelection, setShowEmployeeSelection] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [userRole, setUserRole] = useState(null); // Track user role
  const [showAlert, setShowAlert] = useState('');

  // Helper function to fetch individual department by ID
  const fetchDepartmentById = async (departmentId, token) => {
    try {
      console.log(`FETCHING INDIVIDUAL DEPARTMENT: ${departmentId}`);
      const response = await axios.get(
        `https://crm-tkxn.onrender.com/api/departments/${departmentId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: (status) => status < 500
        }
      );

      if (response.status === 200 && response.data) {
        const deptName = response.data.name || response.data.departmentName || response.data.title;
        console.log(`✅ Individual department fetch successful: ${deptName}`);
        return deptName;
      }

      console.warn(`⚠️ Individual department fetch failed for ID: ${departmentId}`);
      return null;
    } catch (error) {
      console.error(`❌ Error fetching individual department ${departmentId}:`, error);
      return null;
    }
  };

  // Helper function to fetch individual designation by ID
  const fetchDesignationById = async (designationId, token) => {
    try {
      console.log(`FETCHING INDIVIDUAL DESIGNATION: ${designationId}`);
      const response = await axios.get(
        `https://crm-tkxn.onrender.com/api/designations/${designationId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: (status) => status < 500
        }
      );

      if (response.status === 200 && response.data) {
        const desigName = response.data.name || response.data.title || response.data.designationName || response.data.position || response.data.role;
        console.log(`✅ Individual designation fetch successful: ${desigName}`);
        return desigName;
      }

      console.warn(`⚠️ Individual designation fetch failed for ID: ${designationId}`);
      return null;
    } catch (error) {
      console.error(`❌ Error fetching individual designation ${designationId}:`, error);
      return null;
    }
  };

  // NEW: Fetch all employees for selection (for admins) - Now resolves department names
  // NEW: Fetch all employees for selection (for admins) - Now resolves department AND designation names
  const fetchAllEmployees = async (token, companyId, deptMap = {}, designationMap = {}) => {
    try {
      console.log('👥 FETCHING ALL EMPLOYEES FOR SELECTION');
      const response = await axios.get(
        `https://crm-tkxn.onrender.com/api/employees/company/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: (status) => status < 500
        }
      );

      if (response.status === 200 && response.data) {
        let employeesList = Array.isArray(response.data) ? response.data : response.data.employees || [];

        // Filter out inactive employees
        employeesList = employeesList.filter(emp => emp.isActive !== false);

        // Resolve department and designation names for each employee (similar to your existing resolution logic)
        employeesList = await Promise.all(employeesList.map(async (emp) => {
          // Resolve Department (existing logic)
          let resolvedDeptName = 'No Department';
          if (emp.department) {
            if (typeof emp.department === 'object' && emp.department.name) {
              // Already populated object
              resolvedDeptName = emp.department.name || emp.department.departmentName;
            } else if (Array.isArray(emp.department)) {
              // Handle array of department IDs/objects
              const deptNames = [];
              for (let deptId of emp.department) {
                if (typeof deptId === 'string' && deptId.length === 24) {
                  // Try lookup map first
                  let deptName = deptMap[deptId.trim()];
                  if (!deptName) {
                    // Fallback to individual fetch (limited to avoid too many calls)
                    deptName = await fetchDepartmentById(deptId, token);
                  }
                  if (deptName) {
                    deptNames.push(deptName);
                  } else {
                    deptNames.push(`Unknown (${deptId.substring(0, 8)}...)`);
                  }
                } else if (typeof deptId === 'object' && deptId.name) {
                  // Already populated object in array
                  deptNames.push(deptId.name || deptId.departmentName);
                }
              }
              resolvedDeptName = deptNames.length > 0 ? deptNames.join(', ') : 'No Department';
            } else if (typeof emp.department === 'string') {
              // Check if it's an ObjectId or name
              if (emp.department.length === 24) {
                // Likely ID - use map or fetch
                resolvedDeptName = deptMap[emp.department.trim()] || await fetchDepartmentById(emp.department, token) || `Unknown (${emp.department.substring(0, 8)}...)`;
              } else {
                // Likely already a name
                resolvedDeptName = emp.department;
              }
            }
          }

          // NEW: Resolve Designation (mirrors your existing designation resolution logic)
          let resolvedDesigName = 'No Designation';
          if (emp.designation) {
            if (typeof emp.designation === 'object' && (emp.designation.name || emp.designation.title)) {
              // Already populated object
              resolvedDesigName = emp.designation.name || emp.designation.title || emp.designation.designationName;
            } else if (Array.isArray(emp.designation)) {
              // Handle array of designation IDs/objects
              const desigNames = [];
              for (let desigId of emp.designation) {
                if (typeof desigId === 'string' && desigId.length === 24) {
                  // Try lookup map first
                  let desigName = designationMap[desigId.trim()];
                  if (!desigName) {
                    // Fallback to individual fetch (limited to avoid too many calls)
                    desigName = await fetchDesignationById(desigId, token);
                  }
                  if (desigName) {
                    desigNames.push(desigName);
                  } else {
                    desigNames.push(`Unknown (${desigId.substring(0, 8)}...)`);
                  }
                } else if (typeof desigId === 'object' && (desigId.name || desigId.title)) {
                  // Already populated object in array
                  desigNames.push(desigId.name || desigId.title || desigId.designationName);
                }
              }
              resolvedDesigName = desigNames.length > 0 ? desigNames.join(', ') : 'No Designation';
            } else if (typeof emp.designation === 'string') {
              // Check if it's an ObjectId or name
              if (emp.designation.length === 24) {
                // Likely ID - use map or fetch
                resolvedDesigName = designationMap[emp.designation.trim()] || await fetchDesignationById(emp.designation, token) || `Unknown (${emp.designation.substring(0, 8)}...)`;
              } else {
                // Likely already a name
                resolvedDesigName = emp.designation;
              }
            }
          }

          // Add resolved fields to employee object for easy display
          return {
            ...emp,
            resolvedDepartment: resolvedDeptName,
            resolvedDesignation: resolvedDesigName  // NEW: Resolved designation
          };
        }));

        setAllEmployees(employeesList);
        console.log(`✅ Fetched and resolved ${employeesList.length} employees (with depts/desigs) for selection`);
        return employeesList;
      } else {
        console.warn('⚠️ Failed to fetch employees list');
        setAllEmployees([]);
        return [];
      }
    } catch (error) {
      console.error('❌ Error fetching all employees:', error);
      setAllEmployees([]);
      return [];
    }
  };


  // NEW: Handle employee selection (fetch details for selected employee)
  const handleEmployeeSelect = async (selectedEmployeeId) => {
    try {
      setIsLoading(true);
      setError(null);
      setShowEmployeeSelection(false);

      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('No access token found.');

      // Fetch selected employee's details
      console.log(`🚀 FETCHING SELECTED EMPLOYEE: ${selectedEmployeeId}`);
      const employeeResponse = await axios.get(
        `https://crm-tkxn.onrender.com/api/employees/${selectedEmployeeId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: (status) => status < 500
        }
      );

      if (employeeResponse.status !== 200) {
        throw new Error(employeeResponse.data?.message || `Failed to fetch employee data (${employeeResponse.status})`);
      }

      const employee = employeeResponse.data;
      console.log('📊 PROCESSING SELECTED EMPLOYEE DATA:', employee);

      // Extract companyId if needed (for ref data)
      let companyId = employee.company?._id || employee.company?.id || employee.company || employee.companyId;

      // Fetch departments and designations (reuse existing logic)
      const deptResponse = await axios.get(
        `https://crm-tkxn.onrender.com/api/departments/company/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const designationResponse = await axios.get(
        `https://crm-tkxn.onrender.com/api/designations/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Create maps (reuse your existing map creation logic)
      const deptMap = {};
      if (deptResponse.status === 200 && deptResponse.data) {
        const departmentsData = Array.isArray(deptResponse.data) ? deptResponse.data : deptResponse.data.departments || [];
        departmentsData.forEach((dept) => {
          if (dept._id && (dept.name || dept.departmentName)) {
            const deptName = dept.name || dept.departmentName || dept.title;
            deptMap[dept._id] = deptName;
          }
        });
      }
      setDepartments(deptMap);

      const designationMap = {};
      if (designationResponse.status === 200 && designationResponse.data) {
        const designationsData = Array.isArray(designationResponse.data) ? designationResponse.data : designationResponse.data.designations || [];
        designationsData.forEach((designation) => {
          if (designation._id && (designation.name || designation.title || designation.designationName)) {
            const desigName = designation.name || designation.title || designation.designationName || designation.position || designation.role;
            designationMap[designation._id] = desigName;
          }
        });
      }
      setDesignations(designationMap);

      // Resolve department and designation names (reuse your resolution logic)
      let departmentName = 'No Department';
      if (employee.department) {
        if (typeof employee.department === 'object' && employee.department.name) {
          departmentName = employee.department.name || employee.department.departmentName;
        } else if (typeof employee.department === 'string' && employee.department.length === 24) {
          departmentName = deptMap[employee.department] || await fetchDepartmentById(employee.department, token) || `Unknown (${employee.department.substring(0, 8)}...)`;
        } else if (Array.isArray(employee.department)) {
          const names = employee.department.map(deptId =>
            typeof deptId === 'string' ? (deptMap[deptId] || `Unknown (${deptId.substring(0, 8)}...)`) : (deptId.name || 'Unknown')
          );
          departmentName = names.join(', ');
        } else {
          departmentName = employee.department;
        }
      }

      let designationName = 'No Designation';
      if (employee.designation) {
        if (typeof employee.designation === 'object' && (employee.designation.name || employee.designation.title)) {
          designationName = employee.designation.name || employee.designation.title || employee.designation.designationName;
        } else if (typeof employee.designation === 'string' && employee.designation.length === 24) {
          designationName = designationMap[employee.designation] || await fetchDesignationById(employee.designation, token) || `Unknown (${employee.designation.substring(0, 8)}...)`;
        } else if (Array.isArray(employee.designation)) {
          const names = employee.designation.map(desigId =>
            typeof desigId === 'string' ? (designationMap[desigId] || `Unknown (${desigId.substring(0, 8)}...)`) : (desigId.name || 'Unknown')
          );
          designationName = names.join(', ');
        } else {
          designationName = employee.designation;
        }
      }

      // Format date
      const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
      };

      // Update state with selected employee data
      setEmployeeData({
        selectedEmployeeId: employee._id || selectedEmployeeId,
        fullName: employee.teamMemberName || `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || "Not available",
        department: departmentName,
        designation: designationName,
        email: employee.email || "N/A",
        phone: employee.mobileNumber || employee.phone || "N/A",
        emergencyMobileNumber: employee.emergencyMobileNumber || "N/A",
        profileImage: employee.profileImage || "",
        adharImage: employee.adharImage || "",
        panImage: employee.panImage || "",
        aadharNumber: employee.aadharNumber || "",
        panNumber: employee.panNumber || "",
        userUpi: employee.userUpi || "",
        dateOfJoining: formatDate(employee.dateOfJoining),
        employmentStatus: employee.employmentStatus || "N/A",
        reportingManager: employee.reportingManager || employee.managerName || "N/A",
        employmentType: employee.employmentType || "N/A",
        performanceRating: employee.performanceRating || 0,
        skills: employee.skills || [],
        projects: employee.projects || [],
        workHistory: employee.workHistory || [],
        documents: employee.documents || [],
        departmentId: employee.department,
        designationId: employee.designation,
        departmentInfo: null, // Simplified for now
        designationInfo: null,
        attendance: {
          present: 0,
          absent: 0,
          late: 0,
          leaveBalance: {
            sickLeave: 0,
            casualLeave: 0,
            annualLeave: 0
          }
        }
      });

    } catch (error) {
      console.error('Error selecting employee:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load selected employee.');
      setShowEmployeeSelection(true); // Revert to selection on error
    } finally {
      setIsLoading(false);
    }
  };


  // Fetch employee data using JWT token decoding
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setAllEmployees([]);
        setShowEmployeeSelection(false);

        const token = localStorage.getItem('authToken');

        if (!token) {
          throw new Error('No access token found. Please log in.');
        }

        // Decode JWT token to extract role, employeeId, companyId
        let employeeId, companyId, role;
        try {
          const decoded = jwtDecode(token);
          console.log('EMPLOYEE REPORT TOKEN DECODING');
          console.log('✅Full decoded token structure:', decoded);

          role = decoded.role;
          setUserRole(role); // Set role in state

          // Extract employeeId - only for non-admin roles
          if (role !== 'company' && role !== 'superadmin') {
            employeeId = decoded.employeeId || decoded._id || decoded.id;
          }

          // Extract companyId
          companyId = decoded.companyId ||
            decoded.company?._id ||
            decoded.company?.id ||
            decoded.company ||
            decoded.orgId ||
            decoded.organizationId ||
            decoded.id; // For company role, use their own ID as companyId

          console.log('✅Extracted from token:', { role, employeeId, companyId });
        } catch (err) {
          console.error("Error decoding token:", err);
          localStorage.removeItem("authToken");
          throw new Error('Invalid or expired token. Please log in again.');
        }

        if (!companyId) {
          throw new Error('Company information is missing. Please log in again.');
        }
        // ADMIN LOGIC: Company or Superadmin - Show selection
        if (role === 'company' || role === 'superadmin') {
          console.log('ADMIN MODE: Fetching departments, designations, and employees for selection');

          // Fetch departments first to resolve names in employee list
          const deptResponse = await axios.get(
            `https://crm-tkxn.onrender.com/api/departments/company/${companyId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              validateStatus: (status) => status < 500
            }
          );

          if (deptResponse.status === 401) {
            localStorage.removeItem("authToken");
            throw new Error('Session expired. Please log in again.');
          }

          // Create department map
          const deptMap = {};
          if (deptResponse.status === 200 && deptResponse.data) {
            const departmentsData = Array.isArray(deptResponse.data)
              ? deptResponse.data
              : deptResponse.data.departments || [];

            if (Array.isArray(departmentsData)) {
              departmentsData.forEach((dept) => {
                if (dept._id && (dept.name || dept.departmentName)) {
                  const deptName = dept.name || dept.departmentName || dept.title;
                  deptMap[dept._id] = deptName;
                }
              });
            }
          }
          setDepartments(deptMap); // Set for later use in report
          console.log('—‚ï DEPARTMENTS MAP FOR LIST CREATED:', Object.keys(deptMap).length);

          // NEW: Fetch designations to resolve names in employee list
          const designationResponse = await axios.get(
            `https://crm-tkxn.onrender.com/api/designations/`,
            {
              headers: { Authorization: `Bearer ${token}` },
              validateStatus: (status) => status < 500
            }
          );

          if (designationResponse.status === 401) {
            localStorage.removeItem("authToken");
            throw new Error('Session expired. Please log in again.');
          }

          // Create designation map
          const designationMap = {};
          if (designationResponse.status === 200 && designationResponse.data) {
            const designationsData = Array.isArray(designationResponse.data)
              ? designationResponse.data
              : designationResponse.data.designations || [];

            if (Array.isArray(designationsData)) {
              designationsData.forEach((designation) => {
                if (designation._id && (designation.name || designation.title || designation.designationName)) {
                  const desigName = designation.name || designation.title || designation.designationName || designation.position || designation.role;
                  designationMap[designation._id] = desigName;
                }
              });
            }
          }
          setDesignations(designationMap); // Set for later use in report
          console.log('🎯 DESIGNATIONS MAP FOR LIST CREATED:', Object.keys(designationMap).length);

          // Now fetch employees and pass both maps for resolution
          await fetchAllEmployees(token, companyId, deptMap, designationMap); // Pass both maps
          setShowEmployeeSelection(true);
          setIsLoading(false);
          return; // Don't fetch single employee
        }



        // NON-ADMIN LOGIC: Fetch own employee profile (your existing logic - kept intact)
        if (!employeeId) {
          throw new Error('Employee ID is missing from token. Please log in again.');
        }

        // First, fetch employee details to get company information
        console.log('🚀 FETCHING EMPLOYEE DETAILS (NON-ADMIN)');
        const employeeResponse = await axios.get(
          `https://crm-tkxn.onrender.com/api/employees/${employeeId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: (status) => status < 500
          }
        );

        if (employeeResponse.status === 401) {
          localStorage.removeItem("authToken");
          throw new Error('Session expired. Please log in again.');
        }

        if (employeeResponse.status !== 200) {
          throw new Error(employeeResponse.data?.message || `Failed to fetch employee data (${employeeResponse.status})`);
        }

        const employeeDetails = employeeResponse.data;
        console.log('✅Employee details response:', employeeDetails);

        // Extract companyId from employee details if needed
        if (!companyId) {
          companyId = employeeDetails.company?._id ||
            employeeDetails.company?.id ||
            employeeDetails.company ||
            employeeDetails.companyId;
          console.log('✅Extracted companyId from employee details:', companyId);
        }

        // If still no companyId, try localStorage as final fallback
        if (!companyId) {
          console.log('✅CompanyId not found in employee details, trying localStorage fallback...');
          const storedCompanyId = localStorage.getItem('selectedCompanyId');
          if (storedCompanyId) {
            companyId = storedCompanyId;
            console.log('✅Using companyId from localStorage:', companyId);
          }
        }

        if (!companyId) {
          throw new Error('Company information is missing. Please contact support.');
        }

        // Fetch departments using the extracted companyId
        console.log('FETCHING DEPARTMENTS');
        const deptResponse = await axios.get(
          `https://crm-tkxn.onrender.com/api/departments/company/${companyId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: (status) => status < 500
          }
        );

        if (deptResponse.status === 401) {
          localStorage.removeItem("authToken");
          throw new Error('Session expired. Please log in again.');
        }

        // Create a map of department IDs to names
        const deptMap = {};
        console.log('PROCESSING DEPARTMENTS FOR LOOKUP MAP:');
        if (deptResponse.status === 200 && deptResponse.data) {
          const departmentsData = Array.isArray(deptResponse.data)
            ? deptResponse.data
            : deptResponse.data.departments || [];

          if (Array.isArray(departmentsData)) {
            departmentsData.forEach((dept, index) => {
              if (dept._id && (dept.name || dept.departmentName)) {
                const deptName = dept.name || dept.departmentName || dept.title;
                deptMap[dept._id] = deptName;
                console.log(`✅Department ${index + 1}: ${dept._id} †"${deptName}"`);
              } else {
                console.warn(`⚠️ Invalid department data:`, dept);
              }
            });
          }
        }
        setDepartments(deptMap);
        console.log('—‚ï DEPARTMENTS MAP CREATED:', {
          totalDepartments: Object.keys(deptMap).length,
          departmentIds: Object.keys(deptMap),
          departmentNames: Object.values(deptMap)
        });

        // Fetch designations
        console.log('🎯 FETCHING DESIGNATIONS');
        const designationResponse = await axios.get(
          `https://crm-tkxn.onrender.com/api/designations/`,
          {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: (status) => status < 500
          }
        );

        if (designationResponse.status === 401) {
          localStorage.removeItem("authToken");
          throw new Error('Session expired. Please log in again.');
        }

        // Create a map of designation IDs to names
        const designationMap = {};
        console.log('🎯 PROCESSING DESIGNATIONS FOR LOOKUP MAP:');
        console.log('✅Designation API response:', designationResponse.data);

        if (designationResponse.status === 200) {
          // Handle different response structures
          const designationsData = Array.isArray(designationResponse.data)
            ? designationResponse.data
            : designationResponse.data.designations || [];

          if (Array.isArray(designationsData)) {
            designationsData.forEach((designation, index) => {
              if (designation._id && (designation.name || designation.title || designation.designationName)) {
                const desigName = designation.name || designation.title || designation.designationName || designation.position || designation.role;
                designationMap[designation._id] = desigName;
                console.log(`✅Designation ${index + 1}: ${designation._id} †"${desigName}"`);
              } else {
                console.warn(`⚠️ Invalid designation data:`, designation);
              }
            });
          }
        }
        setDesignations(designationMap);
        console.log('—‚ï DESIGNATIONS MAP CREATED:', {
          totalDesignations: Object.keys(designationMap).length,
          designationIds: Object.keys(designationMap),
          designationNames: Object.values(designationMap)
        });

        // Use the already fetched employee details
        const employee = employeeDetails;
        console.log('📊 PROCESSING EMPLOYEE DATA:', employee);

        // Enhanced department resolution (your original logic - kept intact)
        let departmentName = 'No Department';
        let departmentInfo = null;
        let processingMethod = 'none';

        console.log('RESOLVING EMPLOYEE DEPARTMENT INFORMATION:');
        console.log('✅Employee department field:', employee.department);
        console.log('✅Available departments map:', Object.keys(deptMap));

        if (employee.department) {
          if (typeof employee.department === 'object' && !Array.isArray(employee.department) && employee.department.name) {
            // Already populated department object
            departmentName = employee.department.name || employee.department.departmentName;
            departmentInfo = employee.department;
            processingMethod = 'populated_object';
          } else if (Array.isArray(employee.department)) {
            // Handle array of department IDs
            console.log(`Processing multiple departments: ${employee.department.length} departments found`);
            const departmentNames = [];

            for (let i = 0; i < employee.department.length; i++) {
              const deptId = employee.department[i];
              console.log(`✅Processing department ${i + 1}: ${deptId}`);

              if (typeof deptId === 'string' && deptId.length === 24) {
                // Try lookup map first
                let deptName = deptMap[deptId.trim()];
                if (deptName) {
                  departmentNames.push(deptName);
                  console.log(`✅✅ Found in map: ${deptName}`);
                } else {
                  // Try individual fetch
                  console.log(`✅†Not in map, fetching individually: ${deptId}`);
                  const individualDeptName = await fetchDepartmentById(deptId, token);
                  if (individualDeptName) {
                    departmentNames.push(individualDeptName);
                    console.log(`✅✅ Individual fetch successful: ${individualDeptName}`);
                  } else {
                    departmentNames.push(`Unknown Dept (${deptId.substring(0, 8)}...)`);
                    console.log(`✅❌ Individual fetch failed for: ${deptId}`);
                  }
                }
              } else if (typeof deptId === 'object' && deptId.name) {
                // Already populated object in array
                departmentNames.push(deptId.name || deptId.departmentName);
                console.log(`✅✅ Populated object: ${deptId.name || deptId.departmentName}`);
              }
            }

            departmentName = departmentNames.length > 0 ? departmentNames.join(', ') : 'No Department';
            processingMethod = 'array_processing';
            console.log(`Multiple departments resolved: ${departmentName}`);
          } else if (typeof employee.department === 'string') {
            // Check if it's an ObjectId (24 characters) or a name
            if (employee.department.length === 24) {
              // Likely an ObjectId - use lookup map
              departmentInfo = deptMap[employee.department.trim()];
              if (departmentInfo) {
                departmentName = departmentInfo;
                processingMethod = 'lookup_map';
              } else {
                // Try individual fetch for missing department
                console.log(`†Department ID not found in map, attempting individual fetch: ${employee.department}`);
                const individualDeptName = await fetchDepartmentById(employee.department, token);
                if (individualDeptName) {
                  departmentName = individualDeptName;
                  processingMethod = 'individual_fetch';
                } else {
                  departmentName = `Unknown Department (${employee.department.substring(0, 8)}...)`;
                  processingMethod = 'unknown_id';
                }
              }
            } else {
              // Likely already a department name
              departmentName = employee.department;
              processingMethod = 'string_name';
            }
          }
        }

        console.log('DEPARTMENT RESOLUTION COMPLETE:', {
          method: processingMethod,
          originalValue: employee.department,
          resolvedName: departmentName
        });

        // Enhanced designation resolution (your original logic - kept intact)
        let designationName = 'No Designation';
        let designationInfo = null;
        let designationMethod = 'none';

        console.log('🎯 RESOLVING EMPLOYEE DESIGNATION INFORMATION:');
        console.log('✅Employee designation field:', employee.designation);
        console.log('✅Available designations map:', Object.keys(designationMap));

        if (employee.designation) {
          if (typeof employee.designation === 'object' && !Array.isArray(employee.designation) && (employee.designation.name || employee.designation.title)) {
            // Already populated designation object
            designationName = employee.designation.name || employee.designation.title || employee.designation.designationName;
            designationInfo = employee.designation;
            designationMethod = 'populated_object';
          } else if (Array.isArray(employee.designation)) {
            // Handle array of designation IDs
            console.log(`🎯 Processing multiple designations: ${employee.designation.length} designations found`);
            const designationNames = [];

            for (let i = 0; i < employee.designation.length; i++) {
              const desigId = employee.designation[i];
              console.log(`✅Processing designation ${i + 1}: ${desigId}`);

              if (typeof desigId === 'string' && desigId.length === 24) {
                // Try lookup map first
                let desigName = designationMap[desigId.trim()];
                if (desigName) {
                  designationNames.push(desigName);
                  console.log(`✅✅ Found in map: ${desigName}`);
                } else {
                  // Try individual fetch
                  console.log(`✅†Not in map, fetching individually: ${desigId}`);
                  const individualDesigName = await fetchDesignationById(desigId, token);
                  if (individualDesigName) {
                    designationNames.push(individualDesigName);
                    console.log(`✅✅ Individual fetch successful: ${individualDesigName}`);
                  } else {
                    designationNames.push(`Unknown Desig (${desigId.substring(0, 8)}...)`);
                    console.log(`✅❌ Individual fetch failed for: ${desigId}`);
                  }
                }
              } else if (typeof desigId === 'object' && (desigId.name || desigId.title)) {
                // Already populated object in array
                designationNames.push(desigId.name || desigId.title || desigId.designationName);
                console.log(`✅✅ Populated object: ${desigId.name || desigId.title || desigId.designationName}`);
              }
            }

            designationName = designationNames.length > 0 ? designationNames.join(', ') : 'No Designation';
            designationMethod = 'array_processing';
            console.log(`🎯 Multiple designations resolved: ${designationName}`);
          } else if (typeof employee.designation === 'string') {
            // Check if it's an ObjectId (24 characters) or a name
            if (employee.designation.length === 24) {
              // Likely an ObjectId - use lookup map
              designationInfo = designationMap[employee.designation.trim()];
              if (designationInfo) {
                designationName = designationInfo;
                designationMethod = 'lookup_map';
              } else {
                // Try individual fetch for missing designation
                console.log(`†Designation ID not found in map, attempting individual fetch: ${employee.designation}`);
                const individualDesigName = await fetchDesignationById(employee.designation, token);
                if (individualDesigName) {
                  designationName = individualDesigName;
                  designationMethod = 'individual_fetch';
                } else {
                  designationName = `Unknown Designation (${employee.designation.substring(0, 8)}...)`;
                  designationMethod = 'unknown_id';
                }
              }
            } else {
              // Likely already a designation name
              designationName = employee.designation;
              designationMethod = 'string_name';
            }
          }
        }

        console.log('🎯 DESIGNATION RESOLUTION COMPLETE:', {
          method: designationMethod,
          originalValue: employee.designation,
          resolvedName: designationName
        });

        // Format joining date
        const formatDate = (dateString) => {
          if (!dateString || dateString === 'N/A') return 'N/A';
          const options = { year: 'numeric', month: 'long', day: 'numeric' };
          return new Date(dateString).toLocaleDateString('en-US', options);
        };

        setEmployeeData({
          selectedEmployeeId: employee._id || employeeId,
          fullName: employee.teamMemberName || "Not available",
          department: departmentName || "No Department",
          designation: designationName || "No Designation",
          email: employee.email || "N/A",
          phone: employee.mobileNumber || "N/A",
          emergencyMobileNumber: employee.emergencyMobileNumber || "N/A",
          profileImage: employee.profileImage || "",
          adharImage: employee.adharImage || "",
          panImage: employee.panImage || "",
          aadharNumber: employee.aadharNumber || "",
          panNumber: employee.panNumber || "",
          userUpi: employee.userUpi || "",
          dateOfJoining: formatDate(employee.dateOfJoining),
          employmentStatus: employee.employmentStatus || "N/A",
          reportingManager: employee.reportingManager || "N/A",
          employmentType: employee.employmentType || "N/A",
          performanceRating: employee.performanceRating || 0,
          skills: employee.skills || [],
          projects: employee.projects || [],
          workHistory: employee.workHistory || [],
          documents: employee.documents || [],
          departmentId: employee.department,
          designationId: employee.designation,
          departmentInfo: departmentInfo,
          designationInfo: designationInfo,
          attendance: {
            present: 0,
            absent: 0,
            late: 0,
            leaveBalance: {
              sickLeave: 0,
              casualLeave: 0,
              annualLeave: 0
            }
          }
        });

      } catch (error) {
        console.error("Error fetching data:", error);

        // Handle session expiry
        if (error.response?.status === 401) {
          localStorage.removeItem("authToken");
          setError("Session expired. Please log in again.");
        } else {
          // For admins, don't set global error if in selection mode - let modal handle it
          if (!(userRole === 'company' || userRole === 'superadmin') && showEmployeeSelection) {
            setError(error.response?.data?.message || error.message || "Failed to fetch data");
          } else {
            // For non-admins or after selection, show error
            setError(error.response?.data?.message || error.message || "Failed to fetch data");
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmployeeData();
  }, []);

  // Professional PDF Generator with Perfect Layout
  const generatePDF = async () => {
    setIsGeneratingPDF(true);

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (2 * margin);
      let yPosition = margin;

      // Helper function to add text with optional styling
      const addText = (text, x, y, options = {}) => {
        const { fontSize = 10, fontWeight = 'normal', align = 'left', color = '#000000' } = options;

        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', fontWeight);
        pdf.setTextColor(color);

        switch (align) {
          case 'center':
            pdf.text(text, pageWidth / 2, y, { align: 'center' });
            break;
          case 'right':
            pdf.text(text, pageWidth - margin, y, { align: 'right' });
            break;
          default:
            pdf.text(text, x, y);
        }
      };

      // Helper function to add line
      const addLine = (y) => {
        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, y, pageWidth - margin, y);
      };

      // Helper function to check if new page is needed
      const checkNewPage = (heightNeeded) => {
        if (yPosition + heightNeeded > pageHeight - 25) {
          pdf.addPage();
          yPosition = margin + 10;
          return true;
        }
        return false;
      };

      // Helper function to add section box
      const addSectionBox = (title, yStart, height, color = [245, 247, 250]) => {
        pdf.setFillColor(...color);
        pdf.roundedRect(margin, yStart, contentWidth, height, 2, 2, 'F');
      };

      // ========== PROFESSIONAL HEADER ==========
      // Gradient-like header with two colors
      pdf.setFillColor(67, 56, 202); // Indigo
      pdf.rect(0, 0, pageWidth, 35, 'F');
      pdf.setFillColor(99, 102, 241); // Light Indigo
      pdf.rect(0, 0, pageWidth, 25, 'F');

      // Title
      pdf.setFontSize(26);
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.text('EMPLOYEE REPORT', pageWidth / 2, 15, { align: 'center' });

      // Subtitle
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Comprehensive Employee Profile & Analytics', pageWidth / 2, 22, { align: 'center' });

      yPosition = 45;

      // ========== EMPLOYEE OVERVIEW WITH PROFILE ==========
      checkNewPage(60);

      // Section Header with Icon Background
      pdf.setFillColor(239, 246, 255);
      pdf.roundedRect(margin, yPosition, contentWidth, 12, 2, 2, 'F');
      pdf.setFillColor(59, 130, 246);
      pdf.circle(margin + 5, yPosition + 6, 4, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);
      pdf.text('', margin + 2.5, yPosition + 7.5);

      pdf.setTextColor(30, 64, 175);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Employee Information', margin + 12, yPosition + 8);
      yPosition += 18;

      // Profile Photo (if available)
      if (employeeData.profileImage) {
        try {
          const addImageToPDF = async (imageUrl) => {
            try {
              const response = await fetch(imageUrl);
              const blob = await response.blob();
              return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });
            } catch (error) {
              return null;
            }
          };

          const profileImg = await addImageToPDF(employeeData.profileImage);
          if (profileImg) {
            pdf.setDrawColor(59, 130, 246);
            pdf.setLineWidth(0.5);
            pdf.roundedRect(margin + 2, yPosition, 35, 35, 2, 2, 'S');
            pdf.addImage(profileImg, 'JPEG', margin + 3, yPosition + 1, 33, 33);
          }
        } catch (error) {
          console.log('Error loading profile image');
        }
      }

      // Basic Information Table with Better Formatting
      const basicInfoLeft = [
        ['Full Name', employeeData.fullName || 'N/A'],
        ['Employee ID', employeeData.selectedEmployeeId?.substring(0, 20) || 'N/A'],
        ['Department', employeeData.department || 'N/A'],
        ['Designation', employeeData.designation || 'N/A'],
        ['Email', employeeData.email || 'N/A']
      ];

      const basicInfoRight = [
        ['Phone', employeeData.phone || 'N/A'],
        ['Emergency Contact', employeeData.emergencyMobileNumber || 'N/A'],
        ['Date of Joining', employeeData.dateOfJoining || 'N/A'],
        ['Employment Status', employeeData.employmentStatus || 'Active'],
        ['Reporting Manager', employeeData.reportingManager || 'N/A']
      ];

      const startX = employeeData.profileImage ? margin + 42 : margin + 5;
      const infoStartY = yPosition;
      let leftY = infoStartY;

      // Left Column
      basicInfoLeft.forEach(([label, value]) => {
        pdf.setFontSize(8);
        pdf.setTextColor(107, 114, 128);
        pdf.setFont('helvetica', 'normal');
        pdf.text(label.toUpperCase(), startX, leftY);

        pdf.setFontSize(10);
        pdf.setTextColor(31, 41, 55);
        pdf.setFont('helvetica', 'bold');
        const text = value.length > 28 ? value.substring(0, 28) + '...' : value;
        pdf.text(text, startX, leftY + 4);
        leftY += 10;
      });

      // Right Column
      if (contentWidth > 100) {
        let rightY = infoStartY;
        const rightX = startX + 85;

        basicInfoRight.forEach(([label, value]) => {
          pdf.setFontSize(8);
          pdf.setTextColor(107, 114, 128);
          pdf.setFont('helvetica', 'normal');
          pdf.text(label.toUpperCase(), rightX, rightY);

          pdf.setFontSize(10);
          pdf.setTextColor(31, 41, 55);
          pdf.setFont('helvetica', 'bold');
          const text = value.length > 25 ? value.substring(0, 25) + '...' : value;
          pdf.text(text, rightX, rightY + 4);
          rightY += 10;
        });
      }

      yPosition = Math.max(leftY, infoStartY + 50) + 10;

      // ========== IDENTITY & PAYMENT SECTION ==========
      checkNewPage(45);

      // Section Header
      pdf.setFillColor(254, 243, 199);
      pdf.roundedRect(margin, yPosition, contentWidth, 12, 2, 2, 'F');
      pdf.setFillColor(245, 158, 11);
      pdf.circle(margin + 5, yPosition + 6, 4, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);
      pdf.text('💳', margin + 2.5, yPosition + 7.5);

      pdf.setTextColor(146, 64, 14);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Identity & Payment Information', margin + 12, yPosition + 8);
      yPosition += 18;

      // Identity Cards in Boxes
      const identityData = [
        { label: 'Aadhar Number', value: employeeData.aadharNumber || 'Not Provided', color: [219, 234, 254] },
        { label: 'PAN Number', value: employeeData.panNumber || 'Not Provided', color: [243, 232, 255] },
        { label: 'UPI ID', value: employeeData.userUpi || 'Not Provided', color: [220, 252, 231] }
      ];

      const boxWidth = (contentWidth - 10) / 3;
      let xPos = margin;

      identityData.forEach((item, index) => {
        pdf.setFillColor(...item.color);
        pdf.roundedRect(xPos, yPosition, boxWidth, 18, 2, 2, 'F');

        pdf.setFontSize(8);
        pdf.setTextColor(107, 114, 128);
        pdf.setFont('helvetica', 'normal');
        pdf.text(item.label.toUpperCase(), xPos + 3, yPosition + 5);

        pdf.setFontSize(10);
        pdf.setTextColor(31, 41, 55);
        pdf.setFont('helvetica', 'bold');
        const text = item.value.length > 15 ? item.value.substring(0, 15) + '...' : item.value;
        pdf.text(text, xPos + 3, yPosition + 12);

        xPos += boxWidth + 5;
      });

      yPosition += 25;

      // ========== DOCUMENT IMAGES SECTION ==========
      checkNewPage(50);

      // Helper to load images
      const addImageToPDF = async (imageUrl) => {
        try {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          console.error('Error loading image:', error);
          return null;
        }
      };

      // Aadhar Card
      if (employeeData.adharImage) {
        try {
          checkNewPage(75);

          // Section Header
          pdf.setFillColor(219, 234, 254);
          pdf.roundedRect(margin, yPosition, contentWidth, 10, 2, 2, 'F');
          pdf.setFillColor(59, 130, 246);
          pdf.circle(margin + 4, yPosition + 5, 3, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(7);
          pdf.text('📄', margin + 2, yPosition + 6);

          pdf.setTextColor(30, 64, 175);
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Aadhar Card Document', margin + 10, yPosition + 7);
          yPosition += 15;

          const aadharImg = await addImageToPDF(employeeData.adharImage);
          if (aadharImg) {
            const imgWidth = contentWidth - 10;
            const imgHeight = 55;
            pdf.setDrawColor(59, 130, 246);
            pdf.setLineWidth(0.5);
            pdf.roundedRect(margin + 5, yPosition, imgWidth, imgHeight, 2, 2, 'S');
            pdf.addImage(aadharImg, 'JPEG', margin + 6, yPosition + 1, imgWidth - 2, imgHeight - 2);
            yPosition += imgHeight + 10;
          }
        } catch (error) {
          console.log('Error adding Aadhar image');
        }
      }

      // PAN Card
      if (employeeData.panImage) {
        try {
          checkNewPage(75);

          // Section Header
          pdf.setFillColor(243, 232, 255);
          pdf.roundedRect(margin, yPosition, contentWidth, 10, 2, 2, 'F');
          pdf.setFillColor(168, 85, 247);
          pdf.circle(margin + 4, yPosition + 5, 3, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(7);
          pdf.text('📄', margin + 2, yPosition + 6);

          pdf.setTextColor(109, 40, 217);
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text('PAN Card Document', margin + 10, yPosition + 7);
          yPosition += 15;

          const panImg = await addImageToPDF(employeeData.panImage);
          if (panImg) {
            const imgWidth = contentWidth - 10;
            const imgHeight = 55;
            pdf.setDrawColor(168, 85, 247);
            pdf.setLineWidth(0.5);
            pdf.roundedRect(margin + 5, yPosition, imgWidth, imgHeight, 2, 2, 'S');
            pdf.addImage(panImg, 'JPEG', margin + 6, yPosition + 1, imgWidth - 2, imgHeight - 2);
            yPosition += imgHeight + 10;
          }
        } catch (error) {
          console.log('Error adding PAN image');
        }
      }

      // Additional Documents
      if (employeeData.documents && employeeData.documents.length > 0) {
        for (let i = 0; i < employeeData.documents.length; i++) {
          const doc = employeeData.documents[i];
          if (doc.url) {
            try {
              checkNewPage(75);

              // Section Header
              pdf.setFillColor(220, 252, 231);
              pdf.roundedRect(margin, yPosition, contentWidth, 10, 2, 2, 'F');
              pdf.setFillColor(16, 185, 129);
              pdf.circle(margin + 4, yPosition + 5, 3, 'F');
              pdf.setTextColor(255, 255, 255);
              pdf.setFontSize(7);
              pdf.text('Ž', margin + 2, yPosition + 6);

              pdf.setTextColor(6, 95, 70);
              pdf.setFontSize(12);
              pdf.setFont('helvetica', 'bold');
              pdf.text(`${doc.type || `Document ${i + 1}`}`, margin + 10, yPosition + 7);
              yPosition += 15;

              const docImg = await addImageToPDF(doc.url);
              if (docImg) {
                const imgWidth = contentWidth - 10;
                const imgHeight = 55;
                pdf.setDrawColor(16, 185, 129);
                pdf.setLineWidth(0.5);
                pdf.roundedRect(margin + 5, yPosition, imgWidth, imgHeight, 2, 2, 'S');
                pdf.addImage(docImg, 'JPEG', margin + 6, yPosition + 1, imgWidth - 2, imgHeight - 2);
                yPosition += imgHeight + 10;
              }
            } catch (error) {
              console.log(`Error adding document ${i + 1}`);
            }
          }
        }
      }

      yPosition += 5;

      // ========== PROFESSIONAL FOOTER ==========
      const pageCount = pdf.internal.getNumberOfPages();
      const now = new Date();
      const timestamp = now.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);

        // Footer background
        pdf.setFillColor(249, 250, 251);
        pdf.rect(0, pageHeight - 15, pageWidth, 15, 'F');

        // Separator line
        pdf.setDrawColor(229, 231, 235);
        pdf.setLineWidth(0.3);
        pdf.line(margin, pageHeight - 14, pageWidth - margin, pageHeight - 14);

        // Left - Timestamp
        pdf.setFontSize(8);
        pdf.setTextColor(107, 114, 128);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Generated: ${timestamp}`, margin, pageHeight - 8);

        // Center - Company/System Name
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(99, 102, 241);
        pdf.text('Employee Management System', pageWidth / 2, pageHeight - 8, { align: 'center' });

        // Right - Page number
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(107, 114, 128);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
      }

      // ========== OPEN PDF IN NEW WINDOW ==========
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Open PDF in new window/tab for printing
      const newWindow = window.open(pdfUrl, '_blank');
      if (newWindow) {
        newWindow.onload = () => {
          setTimeout(() => {
            newWindow.print();
          }, 250);
        };
      }

      setShowAlert(`✅ PDF report generated successfully for ${employeeData.fullName}`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      setShowAlert('❌ Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Optional: Add this CSS to hide buttons during PDF generation if you want to keep html2canvas option
  const generatePDFWithHTML2Canvas = async () => {
    setIsGeneratingPDF(true);

    try {
      // Create a simplified version of the report for PDF
      const pdfContainer = document.createElement('div');
      pdfContainer.style.cssText = `
      position: fixed;
      left: -9999px;
      top: 0;
      width: 800px;
      padding: 20px;
      background: white;
      font-family: Arial, sans-serif;
    `;

      pdfContainer.innerHTML = `
      <div style="margin-bottom: 30px; text-align: center;">
        <h1 style="color: #2563eb; margin-bottom: 5px;">EMPLOYEE REPORT</h1>
        <div style="height: 2px; background: #2563eb; margin: 10px 0;"></div>
      </div>
      
      <div style="margin-bottom: 20px;">
        <h2 style="color: #1e293b; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px;">Employee Information</h2>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; font-weight: bold; width: 150px;">Full Name:</td>
            <td style="padding: 8px 0;">${employeeData.fullName}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: bold;">Employee ID:</td>
            <td style="padding: 8px 0;">${employeeData.selectedEmployeeId || 'N/A'}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: bold;">Department:</td>
            <td style="padding: 8px 0;">${employeeData.department}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: bold;">Designation:</td>
            <td style="padding: 8px 0;">${employeeData.designation}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: bold;">Email:</td>
            <td style="padding: 8px 0;">${employeeData.email}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: bold;">Phone:</td>
            <td style="padding: 8px 0;">${employeeData.phone}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: bold;">Date of Joining:</td>
            <td style="padding: 8px 0;">${employeeData.dateOfJoining}</td>
          </tr>
        </table>
      </div>
      
      <div style="margin-bottom: 20px;">
        <h2 style="color: #1e293b; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px;">Identity Information</h2>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; font-weight: bold; width: 150px;">Aadhar Number:</td>
            <td style="padding: 8px 0;">${employeeData.aadharNumber || 'Not provided'}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: bold;">PAN Number:</td>
            <td style="padding: 8px 0;">${employeeData.panNumber || 'Not provided'}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: bold;">UPI ID:</td>
            <td style="padding: 8px 0;">${employeeData.userUpi || 'Not provided'}</td>
          </tr>
        </table>
      </div>
      
      <div style="margin-bottom: 20px;">
        <h2 style="color: #1e293b; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px;">Document Status</h2>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; font-weight: bold; width: 150px;">Profile Photo:</td>
            <td style="padding: 8px 0; color: ${employeeData.profileImage ? '#10B981' : '#EF4444'}">
              ${employeeData.profileImage ? '✅Available' : '❌ Not uploaded'}
            </td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: bold;">Aadhar Card:</td>
            <td style="padding: 8px 0; color: ${employeeData.adharImage ? '#10B981' : '#EF4444'}">
              ${employeeData.adharImage ? '✅Available' : '❌ Not uploaded'}
            </td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: bold;">PAN Card:</td>
            <td style="padding: 8px 0; color: ${employeeData.panImage ? '#10B981' : '#EF4444'}">
              ${employeeData.panImage ? '✅Available' : '❌ Not uploaded'}
            </td>
          </tr>
        </table>
      </div>
      
      <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #6b7280;">
        <p>Report generated on ${new Date().toLocaleString()}</p>
        <p>🕒 Employee Management System</p>
      </div>
    `;

      document.body.appendChild(pdfContainer);

      const canvas = await html2canvas(pdfContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      document.body.removeChild(pdfContainer);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
      pdf.save(`Employee_Report_${employeeData.fullName.replace(/\s+/g, '_')}.pdf`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // UPDATED: Render Employee Selection Modal (for admins) - Sophisticated Design with Proper Overlap
  const renderEmployeeSelectionModal = () => {
    if (!showEmployeeSelection) return null;

    // Filter employees based on search
    const filteredEmployees = allEmployees.filter(emp =>
      (emp.teamMemberName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim())
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-fade-in"
        onClick={() => {
          setShowEmployeeSelection(false);
          setAllEmployees([]);
          setSearchTerm('');
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Select an employee to view their report"
      >
        <div
          className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-scale-in border border-gray-100"
          onClick={(e) => e.stopPropagation()}
          style={{ zIndex: '999999' }} // Redundant but ensures inner priority if needed
        >
          {/* Enhanced Header with Gradient */}
          <div className="flex items-center justify-between p-6 bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 text-white border-b border-white/10">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold">Select Employee</h2>
                <p className="text-indigo-100 text-sm">Choose an employee to view their detailed report</p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowEmployeeSelection(false);
                setAllEmployees([]);
                setSearchTerm('');
              }}
              className="p-2 hover:bg-white/20 rounded-2xl transition-all duration-200 group backdrop-blur-sm"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5 text-white group-hover:rotate-90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Enhanced Search with Clear Button */}
          <div className="p-6 border-b border-gray-200 bg-gray-50/50">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 pr-10 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                aria-label="Search employees"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                  aria-label="Clear search"
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Enhanced Employees List */}
          <div className="p-6 overflow-y-auto max-h-[50vh] bg-white">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <div className="relative">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                  <div className="absolute inset-0 rounded-full border-2 border-gray-200 animate-pulse"></div>
                </div>
                <p className="text-gray-600 font-medium">Loading employees...</p>
                <p className="text-sm text-gray-500">Please wait while we fetch the latest data</p>
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">No employees found</h3>
                  <p className="text-gray-500 max-w-sm">
                    {searchTerm ? 'Try adjusting your search terms.' : 'No employees available at the moment.'}
                  </p>
                </div>
                {!searchTerm && (
                  <button
                    onClick={() => {
                      const token = localStorage.getItem('authToken');
                      if (token) {
                        const decoded = jwtDecode(token);
                        const companyId = decoded.companyId || decoded.company?._id || decoded.id;
                        fetchAllEmployees(token, companyId, departments, designations);
                      }
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors"
                  >
                    Refresh List
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredEmployees.map((emp) => {
                  const empName = emp.teamMemberName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
                  const empId = emp._id || emp.id;
                  return (
                    <button
                      key={empId}
                      onClick={() => handleEmployeeSelect(empId)}
                      className="w-full flex items-center space-x-4 p-4 bg-white rounded-2xl border border-gray-200 hover:border-indigo-300 hover:shadow-md hover:scale-[1.02] transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    >
                      {/* Avatar */}
                      <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:shadow-xl transition-shadow">
                        <span className="text-white font-bold text-lg">
                          {empName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </span>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0 text-left" >
                        <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
                          {empName || 'Unnamed Employee'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{emp.email || 'No email'}</p>
                      </div>

                      {/* Details */}
                      <div className="text-right flex-shrink-0 space-y-1">
                        <p className="text-xs font-medium text-gray-700 flex items-center justify-end">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2H4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.586l-1.414-1.414a2 2 0 00-2.828 0L11.586 14H9a2 2 0 00-2 2v4a2 2 0 002 2z" />
                          </svg>
                          {emp.resolvedDesignation || 'No designation'}
                        </p>
                        <p className="text-xs text-gray-600 flex items-center justify-end">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          {emp.resolvedDepartment || 'No Department'}
                        </p>
                        <p className="text-xs font-medium text-indigo-600">{empId.substring(0, 8)}...</p>
                      </div>

                      {/* Chevron Icon */}
                      <svg className="w-4 h-4 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Enhanced Footer with Gradient */}
          <div className="p-6 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-indigo-50">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <p className="text-sm text-gray-600 font-medium">
                Showing <span className="text-indigo-600 font-semibold">{filteredEmployees.length}</span> of <span className="text-indigo-600 font-semibold">{allEmployees.length}</span> employees
              </p>
              <div className="flex space-x-3 w-full sm:w-auto">
                <button
                  onClick={() => setSearchTerm('')}
                  className="flex-1 sm:flex-none px-4 py-2 text-sm text-gray-700 hover:text-gray-900 font-medium transition-colors"
                  disabled={!searchTerm}
                >
                  Clear Search
                </button>
                <button
                  onClick={async () => {
                    const token = localStorage.getItem('authToken');
                    if (token) {
                      try {
                        setIsLoading(true);
                        const decoded = jwtDecode(token);
                        const companyId = decoded.companyId || decoded.company?._id || decoded.id;
                        await fetchAllEmployees(token, companyId, departments, designations);
                        setIsLoading(false);
                      } catch (error) {
                        console.error('Error refreshing employees:', error);
                        setIsLoading(false);
                      }
                    }
                  }}
                  className="flex-1 sm:flex-none px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoading}
                >
                  {isLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };





  // Function to open image modal
  const openImageModal = (imageUrl, title) => {
    setImageModal({ isOpen: true, imageUrl, title });
  };

  // Function to close image modal
  const closeImageModal = () => {
    setImageModal({ isOpen: false, imageUrl: '', title: '' });
  };

  // Handle keyboard events for modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && imageModal.isOpen) {
        closeImageModal();
      }
    };

    if (imageModal.isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [imageModal.isOpen]);

  // Function to handle exporting report
  const exportReport = (format) => {
    setShowAlert(`Exporting employee report as ${format.toUpperCase()} for ${employeeData.fullName}`);
    // In a real application, this would generate and download the report
  };

  if (isLoading && !showEmployeeSelection) {  // Only show global loading if not in admin selection mode
    // return (
    //   <div className="flex justify-center items-center h-64">
    //     <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    //   </div>
    // );
  }

  if (error && !showEmployeeSelection) {  // Only show global error if not in admin selection mode
    return (
      <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
        <p>Error: {error}</p>
        <p className="mt-2 text-sm">Please make sure you're logged in and have selected an employee.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-2 md:p-4">
      <div className="max-w-full mx-auto">

        {renderEmployeeSelectionModal()}

        <div className="mb-4">
          {/* Header removed as requested */}
        </div>

        {/* PDF Content Wrapper */}
        <div id="employee-report-content">
          {/* Enhanced Employee Summary Card */}
          <div className="grid grid-cols-1 gap-4 mb-6">
            <div className="bg-white rounded-2xl shadow-xl p-6 animate-fade-in-up border border-gray-100">
              <div className="flex flex-col lg:flex-row items-center lg:items-start">
                {/* Profile Image Section */}
                <div className="relative mb-6 lg:mb-0 lg:mr-8">
                  <div className="w-32 h-32 bg-gradient-to-br from-indigo-400 via-purple-400 to-blue-400 rounded-2xl flex items-center justify-center overflow-hidden shadow-2xl ring-4 ring-white">
                    {employeeData.profileImage ? (
                      <img
                        src={employeeData.profileImage}
                        alt={employeeData.fullName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '';
                          e.target.parentElement.classList.add('bg-gradient-to-br', 'from-indigo-400', 'via-purple-400', 'to-blue-400');
                          e.target.parentElement.innerHTML = `
                          <span class="text-white text-4xl font-bold">
                            ${employeeData.fullName.split(' ').map(name => name[0]).join('')}
                          </span>
                        `;
                        }}
                      />
                    ) : (
                      <span className="text-white text-4xl font-bold">
                        {employeeData.fullName.split(' ').map(name => name[0]).join('')}
                      </span>
                    )}
                  </div>
                  {/* Status Badge */}
                  <div className="absolute -bottom-2 -right-2">
                    <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg flex items-center">
                      <div className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></div>
                      Active
                    </div>
                  </div>
                </div>

                {/* Employee Info Section */}
                <div className="flex-1 text-center lg:text-left">
                  <div className="mb-4">
                    <h2 className="text-3xl font-bold text-gray-800 mb-2">{employeeData.fullName}</h2>
                    <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
                      <div></div>

                      {/* Action Buttons on the Far Right */}
                      <div className="flex items-center gap-3">
                        <button
                          onClick={generatePDF}
                          disabled={isGeneratingPDF}
                          className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md transition-all duration-200 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                          <span>Print Report</span>
                        </button>

                        {(userRole === 'company' || userRole === 'superadmin') && (
                          <button
                            onClick={() => {
                              setShowEmployeeSelection(true);
                              setEmployeeData({
                                fullName: "",
                                selectedEmployeeId: "",
                                department: "",
                                designation: "",
                                email: "",
                                phone: "",
                                emergencyMobileNumber: "",
                                profileImage: "",
                                adharImage: "",
                                panImage: "",
                                aadharNumber: "",
                                panNumber: "",
                                userUpi: "",
                                dateOfJoining: "",
                                employmentStatus: "",
                                reportingManager: "",
                                employmentType: "",
                                performanceRating: 0,
                                skills: [],
                                projects: [],
                                workHistory: [],
                                documents: [],
                                attendance: {
                                  present: 0,
                                  absent: 0,
                                  late: 0,
                                  leaveBalance: {
                                    sickLeave: 0,
                                    casualLeave: 0,
                                    annualLeave: 0
                                  }
                                }
                              });
                              const token = localStorage.getItem('authToken');
                              if (token) {
                                const decoded = jwtDecode(token);
                                const companyId = decoded.companyId || decoded.company?._id || decoded.id;
                                fetchAllEmployees(token, companyId, departments, designations);
                              }
                            }}
                            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold shadow-md transition-all duration-200"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            </svg>
                            <span>Change Employee</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Quick Info Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 112 0v1m-2 0h4m-4 0a2 2 0 00-2 2v10a2 2 0 002 2h4a2 2 0 002-2V9a2 2 0 00-2-2m-4 0V6a2 2 0 012-2h4a2 2 0 012 2v4" />
                          </svg>
                        </div>
                        <div className="relative group">
                          <p className="text-xs text-gray-500 font-medium">Employee ID</p>
                          <p className="font-bold text-gray-800 truncate max-w-full cursor-help">
                            {employeeData.selectedEmployeeId?.substring(0, 10)}...
                          </p>
                          {employeeData.selectedEmployeeId && (
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible whitespace-nowrap z-10 shadow-lg transition-all duration-200" style={{ zIndex: '99999999' }}>
                              {employeeData.selectedEmployeeId}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 112 0v1m-2 0h4m-4 0a2 2 0 00-2 2v10a2 2 0 002 2h4a2 2 0 002-2V9a2 2 0 00-2-2m-4 0V6a2 2 0 012-2h4a2 2 0 012 2v4" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Joined</p>
                          <p className="font-bold text-gray-800">{employeeData.dateOfJoining}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-100">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Tenure</p>
                          <p className="font-bold text-gray-800">
                            {employeeData.dateOfJoining && employeeData.dateOfJoining !== 'N/A'
                              ? `${new Date().getFullYear() - new Date(employeeData.dateOfJoining).getFullYear()} years`
                              : 'N/A'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="bg-white rounded-2xl shadow-xl p-6 animate-fade-in border border-gray-100">
            <div className="space-y-6">

              {/* Enhanced Information Grid */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Contact Information Card */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-100">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">Contact Information</h3>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-blue-100">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email Address</p>
                          <p className="text-gray-800 font-medium">{employeeData.email}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-xl shadow-sm border border-blue-100">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone Number</p>
                          <p className="text-gray-800 font-medium">{employeeData.phone}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-xl shadow-sm border border-blue-100">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Emergency Contact</p>
                          <p className="text-gray-800 font-medium">{employeeData.emergencyMobileNumber}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Employment Information Card */}
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl border border-purple-100">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 00-2 2H8a2 2 0 00-2-2V6m8 0h2a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h2" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">Employment Details</h3>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-purple-100">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Department</p>
                          <p className="text-gray-800 font-medium">{employeeData.department}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-xl shadow-sm border border-purple-100">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-4">
                          <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 00-2 2H8a2 2 0 00-2-2V6m8 0h2a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h2" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Designation</p>
                          <p className="text-gray-800 font-medium">{employeeData.designation}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-xl shadow-sm border border-purple-100">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 112 0v1m-2 0h4m-4 0a2 2 0 00-2 2v10a2 2 0 002 2h4a2 2 0 002-2V9a2 2 0 00-2-2m-4 0V6a2 2 0 012-2h4a2 2 0 012 2v4" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date of Joining</p>
                          <p className="text-gray-800 font-medium">{employeeData.dateOfJoining}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Identity & Payment Information */}
              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-6 rounded-2xl border border-orange-100">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V4a2 2 0 114 0v2m-4 0a2 2 0 104 0m-4 0V4a2 2 0 014 0v2" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Identity & Payment Information</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-orange-100">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V4a2 2 0 114 0v2m-4 0a2 2 0 104 0m-4 0V4a2 2 0 014 0v2" />
                        </svg>
                      </div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Aadhar Number</p>
                      <p className="text-gray-800 font-mono text-sm">
                        {employeeData.aadharNumber ?
                          `${employeeData.aadharNumber.slice(0, 4)} ${employeeData.aadharNumber.slice(4, 8)} ${employeeData.aadharNumber.slice(8)}` :
                          "Not provided"
                        }
                      </p>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-xl shadow-sm border border-orange-100">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">PAN Number</p>
                      <p className="text-gray-800 font-mono text-sm uppercase">
                        {employeeData.panNumber || "Not provided"}
                      </p>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-xl shadow-sm border border-orange-100">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">UPI ID</p>
                      <p className="text-gray-800 font-mono text-sm">
                        {employeeData.userUpi || "Not provided"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Document Images Section */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-6">📄 Identity Documents</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  {/* Aadhar Card Image */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V4a2 2 0 114 0v2m-4 0a2 2 0 104 0m-4 0V4a2 2 0 014 0v2" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-800">Aadhar Card</h4>
                    </div>

                    {employeeData.adharImage ? (
                      <div
                        className="relative group cursor-pointer"
                        onClick={() => openImageModal(employeeData.adharImage, 'Aadhar Card')}
                      >
                        <img
                          src={employeeData.adharImage}
                          alt="Aadhar Card"
                          className="w-full h-48 object-cover rounded-lg border-2 border-blue-200 shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer"
                          onClick={() => openImageModal(employeeData.adharImage, 'Aadhar Card')}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-300 flex items-center justify-center pointer-events-none">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                        </div>
                        <div className="mt-3 text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Document Available
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-48 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center">
                        <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-gray-500 text-sm font-medium">No Aadhar Card Image</p>
                        <p className="text-gray-400 text-xs mt-1">Document not uploaded</p>
                      </div>
                    )}
                  </div>

                  {/* PAN Card Image */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-100">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-800">PAN Card</h4>
                    </div>

                    {employeeData.panImage ? (
                      <div
                        className="relative group cursor-pointer"
                        onClick={() => openImageModal(employeeData.panImage, 'PAN Card')}
                      >
                        <img
                          src={employeeData.panImage}
                          alt="PAN Card"
                          className="w-full h-48 object-cover rounded-lg border-2 border-purple-200 shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer"
                          onClick={() => openImageModal(employeeData.panImage, 'PAN Card')}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-300 flex items-center justify-center pointer-events-none">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                        </div>
                        <div className="mt-3 text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Document Available
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-48 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center">
                        <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-gray-500 text-sm font-medium">No PAN Card Image</p>
                        <p className="text-gray-400 text-xs mt-1">Document not uploaded</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Documents Section */}
                {employeeData.documents && employeeData.documents.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-xl font-semibold text-gray-800 mb-6">Ž Additional Documents</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {employeeData.documents.map((doc, index) => {
                        // Generate different gradient colors for variety
                        const gradients = [
                          { from: 'from-emerald-50', to: 'to-teal-50', border: 'border-emerald-100', iconBg: 'bg-emerald-500', imgBorder: 'border-emerald-200' },
                          { from: 'from-amber-50', to: 'to-orange-50', border: 'border-amber-100', iconBg: 'bg-amber-500', imgBorder: 'border-amber-200' },
                          { from: 'from-cyan-50', to: 'to-sky-50', border: 'border-cyan-100', iconBg: 'bg-cyan-500', imgBorder: 'border-cyan-200' },
                          { from: 'from-rose-50', to: 'to-pink-50', border: 'border-rose-100', iconBg: 'bg-rose-500', imgBorder: 'border-rose-200' },
                        ];
                        const gradient = gradients[index % gradients.length];

                        return (
                          <div key={index} className={`bg-gradient-to-br ${gradient.from} ${gradient.to} p-6 rounded-xl border ${gradient.border}`}>
                            <div className="flex items-center mb-4">
                              <div className={`w-10 h-10 ${gradient.iconBg} rounded-lg flex items-center justify-center mr-3`}>
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                              <h4 className="text-lg font-semibold text-gray-800">{doc.type || `Document ${index + 1}`}</h4>
                            </div>

                            {doc.url ? (
                              <div
                                className="relative group cursor-pointer"
                                onClick={() => openImageModal(doc.url, doc.type || `Document ${index + 1}`)}
                              >
                                <img
                                  src={doc.url}
                                  alt={doc.type || `Document ${index + 1}`}
                                  className={`w-full h-48 object-cover rounded-lg border-2 ${gradient.imgBorder} shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer`}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE4IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+RG9jdW1lbnQ8L3RleHQ+PC9zdmc+';
                                  }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-300 flex items-center justify-center pointer-events-none">
                                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                  </div>
                                </div>
                                <div className="mt-3 text-center">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Document Available
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="w-full h-48 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center">
                                <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <p className="text-gray-500 text-sm font-medium">No Document Image</p>
                                <p className="text-gray-400 text-xs mt-1">Document not uploaded</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
        {/* End PDF Content Wrapper */}
      </div>

      {/* Image Modal Popup */}
      {imageModal.isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm animate-fade-in"
          onClick={closeImageModal}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
              <h3 className="text-xl font-semibold flex items-center">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {imageModal.title}
              </h3>
              <button
                onClick={closeImageModal}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-all duration-200 group"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6 group-hover:rotate-90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 bg-gray-50 max-h-[70vh] overflow-auto">
              <div className="relative flex justify-center">
                <img
                  src={imageModal.imageUrl}
                  alt={imageModal.title}
                  className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
                />

                {/* Download Button */}
                <div className="absolute top-4 right-4">
                  <a
                    href={imageModal.imageUrl}
                    download={`${imageModal.title.replace(' ', '_')}.jpg`}
                    className="bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 p-3 rounded-full shadow-lg transition-all duration-200 flex items-center justify-center group"
                    title="Download Image"
                  >
                    <svg className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-white border-t border-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                📱 Click outside or press <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">ESC</kbd> to close
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => window.open(imageModal.imageUrl, '_blank')}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200 flex items-center text-sm"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Open in New Tab
                </button>
                <button
                  onClick={closeImageModal}
                  className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200 text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inline CSS for animations */}
      <style>
        {`
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes fade-in-up {
            from { 
              opacity: 0;
              transform: translateY(20px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fade-in {
            animation: fade-in 0.5s ease-out;
          }
          .animate-fade-in-up {
            animation: fade-in-up 0.5s ease-out;
          }
        `}
      </style>
    </div>
  );
};

export default EmployeeReport;

